import { Button, DatePicker, Notice, Select, Text, VStack } from '@genial/components';
import type { ReportingEnumsReportingFilePeriodEnum } from '@genial/services/dist/models';
import { Input as NativeInput, ScrollView } from 'native-base';
import React, { useEffect } from 'react';

import {
  isFeeSettlementDocumentType,
  isProfitabilityDocumentType,
} from '../../../../../store/reportGenerator/displayParameters';
import { isReferenceDatePeriod } from '../../../../../store/reportGenerator/periods';
import { ReportGeneratorRequestModalFormProps } from '../types';

import { BranchAdvisorFields } from './BranchAdvisorFields';
import {
  PROFITABILITY_CUSTOMER_PARAM_NAMES,
  ProfitabilityCustomerField,
} from './ProfitabilityCustomerField';
import { ProfitabilityPeriodFields } from './ProfitabilityPeriodFields';
import {
  RequestModalFieldShell,
  requestModalDatePickerInputProps,
  requestModalFieldPadding,
  requestModalInputFontSize,
  requestModalSelectTriggerProps,
  requestModalSelectTriggerTextProps,
} from './requestModalFieldStyles';
import { RequestSubmitLoadingDots } from './RequestSubmitLoadingDots';

const isCustomerParamName = (name: string) =>
  (PROFITABILITY_CUSTOMER_PARAM_NAMES as readonly string[]).includes(name);

const RequestParametersFields: React.FC<
  Pick<
    ReportGeneratorRequestModalFormProps,
    'parameterDefinitions' | 'additionalParameters' | 'updateAdditionalParameter'
  > & { excludeCustomerParams?: boolean }
> = ({
  parameterDefinitions,
  additionalParameters,
  updateAdditionalParameter,
  excludeCustomerParams = false,
}) => {
  const definitions = excludeCustomerParams
    ? parameterDefinitions.filter((parameter) => !isCustomerParamName(parameter.name))
    : parameterDefinitions;

  if (!definitions.length) {
    return null;
  }

  return (
    <VStack space="stack.xxxs">
      {definitions.map((parameter) => (
        <VStack key={parameter.name} space="stack.quarck">
          <Text variant="regular">{parameter.label}</Text>
          {parameter.options.length ? (
            <RequestModalFieldShell>
              <Select
                testID={`report-generator-request-parameter-${parameter.name}`}
                selectedValue={additionalParameters[parameter.name] ?? ''}
                onValueChange={(value) => updateAdditionalParameter(parameter.name, value)}
                options={parameter.options}
                placeholder={`Selecione ${parameter.label.toLowerCase()}`}
                triggerProps={requestModalSelectTriggerProps}
                triggerTextProps={requestModalSelectTriggerTextProps}
              />
            </RequestModalFieldShell>
          ) : (
            <RequestModalFieldShell>
              <NativeInput
                testID={`report-generator-request-parameter-${parameter.name}`}
                value={additionalParameters[parameter.name] ?? ''}
                onChangeText={(value) => updateAdditionalParameter(parameter.name, value)}
                placeholder={parameter.label}
                variant="unstyled"
                fontSize={requestModalInputFontSize}
                p={requestModalFieldPadding}
              />
            </RequestModalFieldShell>
          )}
        </VStack>
      ))}
    </VStack>
  );
};

const RequestPeriodFields: React.FC<
  Pick<
    ReportGeneratorRequestModalFormProps,
    'period' | 'periodOptions' | 'referenceDate' | 'updatePeriod' | 'updateReferenceDate'
  >
> = ({ period, periodOptions, referenceDate, updatePeriod, updateReferenceDate }) => {
  if (!periodOptions.length) {
    return null;
  }

  return (
    <VStack space="stack.xxxs">
      <VStack space="stack.quarck">
        <Text variant="regular">Período</Text>
        <RequestModalFieldShell>
          <Select
            testID="report-generator-request-period-select"
            selectedValue={period ?? ''}
            onValueChange={(value) => updatePeriod(value as ReportingEnumsReportingFilePeriodEnum)}
            options={periodOptions}
            placeholder="Selecione um período"
            triggerProps={requestModalSelectTriggerProps}
            triggerTextProps={requestModalSelectTriggerTextProps}
          />
        </RequestModalFieldShell>
      </VStack>
      {isReferenceDatePeriod(period) ? (
        <VStack space="stack.quarck">
          <Text variant="regular">Data de referência</Text>
          <RequestModalFieldShell>
            <DatePicker
              date={referenceDate}
              onClear={() => updateReferenceDate(undefined)}
              onSelectDate={(date) => updateReferenceDate(date)}
              calendarPosition="left"
              placeholder="DD/MM/AAAA"
              inputComponentProps={requestModalDatePickerInputProps}
            />
          </RequestModalFieldShell>
        </VStack>
      ) : null}
    </VStack>
  );
};

const RequestDocumentTypeField: React.FC<
  Pick<
    ReportGeneratorRequestModalFormProps,
    'documentType' | 'documentTypeOptions' | 'updateDocumentType'
  >
> = ({ documentType, documentTypeOptions, updateDocumentType }) => {
  if (!documentTypeOptions.length) {
    return null;
  }

  return (
    <VStack space="stack.quarck">
      <Text variant="regular">Tipo de documento</Text>
      <RequestModalFieldShell>
        <Select
          testID="report-generator-request-document-type-select"
          selectedValue={documentType ?? ''}
          onValueChange={updateDocumentType}
          options={documentTypeOptions}
          placeholder="Selecione o tipo de documento"
          triggerProps={requestModalSelectTriggerProps}
          triggerTextProps={requestModalSelectTriggerTextProps}
        />
      </RequestModalFieldShell>
    </VStack>
  );
};

const ProfitabilityRequestFields: React.FC<ReportGeneratorRequestModalFormProps> = ({
  period,
  updatePeriod,
  additionalParameters,
  parameterDefinitions,
  updateAdditionalParameter,
  referenceDate,
  updateReferenceDate,
}) => {
  useEffect(() => {
    if (!isReferenceDatePeriod(period)) {
      updatePeriod('ReferenceDate');
    }
  }, [period, updatePeriod]);

  return (
    <>
      <ProfitabilityCustomerField
        additionalParameters={additionalParameters}
        updateAdditionalParameter={updateAdditionalParameter}
      />
      <RequestParametersFields
        parameterDefinitions={parameterDefinitions}
        additionalParameters={additionalParameters}
        updateAdditionalParameter={updateAdditionalParameter}
        excludeCustomerParams
      />
      <ProfitabilityPeriodFields
        referenceDate={referenceDate}
        updateReferenceDate={updateReferenceDate}
      />
    </>
  );
};

const StandardRequestFields: React.FC<ReportGeneratorRequestModalFormProps> = (props) => {
  const isFeeSettlement = isFeeSettlementDocumentType(Number(props.documentType));

  return (
    <>
      {isFeeSettlement ? (
        <BranchAdvisorFields
          partnerId={props.requestPartnerId}
          advisorId={props.requestAdvisorId}
          onChangePartner={props.updateRequestPartnerId}
          onChangeAdvisor={props.updateRequestAdvisorId}
        />
      ) : null}
      <RequestParametersFields {...props} />
      <RequestPeriodFields {...props} />
    </>
  );
};

export const RequestModalFormStacked: React.FC<ReportGeneratorRequestModalFormProps> = (props) => {
  const isProfitability = isProfitabilityDocumentType(Number(props.documentType));

  return (
    <ScrollView flex={1} minH={0} showsVerticalScrollIndicator={false}>
      <VStack bg="neutral.white" px="inline.xxs" pb="inline.xxs" space="stack.xxxs">
        <Notice
          type="alert"
          testID="report-generator-request-beta-notice"
          backgroundColor="#DBEAFF73"
          fontSize="14px"
          textObjects={[
            { text: <Text>Versão Beta: </Text>, variant: 'semibold' },
            {
              text: <Text>Você está gerando o novo relatório, disponível em versão Beta.</Text>,
              variant: 'regular',
            },
          ]}
        />
        <RequestDocumentTypeField {...props} />
        {isProfitability ? (
          <ProfitabilityRequestFields {...props} />
        ) : (
          <StandardRequestFields {...props} />
        )}
      </VStack>
    </ScrollView>
  );
};

export const RequestModalFooter: React.FC<{
  loading: boolean;
  canRequestExport: boolean;
  onRequestExport: () => void;
}> = ({ loading, canRequestExport, onRequestExport }) => (
  <Button
    w="full"
    variant="primary"
    onPress={onRequestExport}
    isDisabled={!canRequestExport || loading}
    _text={{ textTransform: 'none' }}>
    {loading ? <RequestSubmitLoadingDots /> : 'Solicitar'}
  </Button>
);
